import { createBrowserRouter, RouterProvider, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../components/layout/Navbar';
import BottomNav from '../components/layout/BottomNav';
import Login from '../features/auth/LoginPage';
import SignUp from '../features/auth/SignUpPage';
import ForgotPassword from '../features/auth/ForgotPasswordPage';
import ResetPassword from '../features/auth/ResetPasswordPage';
import HomePage from '../pages/HomePage';
import AttendancePage from '../features/attendance/AttendancePage';
import AddShiftReport from '../features/reports/AddShiftReportPage';
import AddMonthlyReport from '../features/reports/AddMonthlyReportPage';
import ViewReports from '../features/reports/ViewReportsPage';
import ViewShiftReport from '../features/reports/ViewShiftReportPage';
import ViewMonthlyReport from '../features/reports/ViewMonthlyReportPage';
import Settings from '../pages/SettingsPage';
import PASInterface from '../features/pas/PASMainPage';
import QDFMainPage from '../features/qdf/QDFMainPage';
import QDFForm from '../features/qdf/QDFForm';
import QDFSettingsPage from '../features/qdf/QDFSettingsPage';
import RCAMainPage from '../features/root-cause-analysis/RCAMainPage';
import MaintenanceAnalysisPage from '../features/maintenance-analysis/MaintenanceAnalysisPage';
import SystemReportPage from '../features/system-report/SystemReportPage';
import { AuthProvider } from '../contexts/AuthContext';
import { SessionProvider } from '../contexts/SessionContext';
import InactivityModal from '../features/auth/InactivityModal';
import LogoutNotification from '../features/auth/LogoutNotification';
import ProtectedRoute from '../components/layout/ProtectedRoute';
import AdminRoute from '../components/layout/AdminRoute';
import '../styles/toast.css';

// Auth screens own the full viewport, so the app chrome stays out of their way.
const CHROMELESS_ROUTES = ['/login', '/signup', '/forgot-password', '/reset-password'];

function RoutesWithAuth() {
  return (
    <Routes>
      <Route path="/signup" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/add-shift-report" element={<ProtectedRoute><AddShiftReport /></ProtectedRoute>} />
      <Route path="/add-monthly-report" element={<AdminRoute><AddMonthlyReport /></AdminRoute>} />
      <Route path="/viewReports" element={<ProtectedRoute><ViewReports /></ProtectedRoute>} />
      <Route path="/view-shift-report/:id" element={<ProtectedRoute><ViewShiftReport /></ProtectedRoute>} />
      <Route path="/view-monthly-report/:id" element={<AdminRoute><ViewMonthlyReport /></AdminRoute>} />
      <Route path="/qdf-main" element={<ProtectedRoute><QDFMainPage /></ProtectedRoute>} />
      <Route path="/qdf-settings" element={<AdminRoute><QDFSettingsPage /></AdminRoute>} />
      <Route path="/qdf-ppm-form" element={<ProtectedRoute><QDFForm /></ProtectedRoute>} />
      <Route path="/qdf-form" element={<ProtectedRoute><QDFForm /></ProtectedRoute>} />
      <Route path="/qdf-public/:token" element={<QDFForm />} />
      <Route path="/pas-interface" element={<ProtectedRoute><PASInterface /></ProtectedRoute>} />
      <Route path="/rca-main" element={<AdminRoute><RCAMainPage /></AdminRoute>} />
      <Route path="/maintenance-analysis" element={<AdminRoute><MaintenanceAnalysisPage /></AdminRoute>} />
      <Route path="/system-report" element={<AdminRoute><SystemReportPage /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

function AppChrome() {
  const { pathname } = useLocation();
  // The public QDF request form has no session, so it gets no app chrome either.
  const chromeless =
    CHROMELESS_ROUTES.includes(pathname) || pathname.startsWith('/qdf-public/');

  return (
    <>
      {!chromeless && <Navbar />}
      <RoutesWithAuth />
      {!chromeless && <BottomNav />}
      <InactivityModal />
      <LogoutNotification />
    </>
  );
}

const router = createBrowserRouter([{ path: '*', element: <AppChrome /> }]);

function App() {
  return (
    <AuthProvider>
      <SessionProvider>
        <RouterProvider router={router} />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          // Lifts toasts above the mobile tab bar; see styles/toast.css.
          className="rga-toast-container"
        />
      </SessionProvider>
    </AuthProvider>
  );
}

export default App;
